import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { learners as api } from "../../../../../api/learners";

const avatarColours = [
  "bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500",
  "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-red-500",
];
const getAvatarColour = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColours[Math.abs(hash) % avatarColours.length];
};
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

const StatusBadge = ({ status }) => {
  const map = {
    completed: "text-green-600 bg-green-100 border border-green-200",
    "in progress": "text-orange-600 bg-orange-100 border border-orange-200",
    "not started": "text-gray-500 bg-gray-100 border border-gray-200",
    active: "text-blue-600 bg-blue-100 border border-blue-200",
  };
  const cls = map[status?.toLowerCase()] ?? "text-gray-500 bg-gray-100 border border-gray-200";
  return (
    <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${cls}`}>
      {status ?? "—"}
    </span>
  );
};

const LearnerProfile = () => {
  const { learnerId } = useParams();
  const navigate = useNavigate();

  // Try sessionStorage first for instant render, then verify via API
  const cached = (() => {
    try { return JSON.parse(sessionStorage.getItem("currentLearner")); } catch { return null; }
  })();

  const { data: response, isLoading } = useQuery(
    ["learner-profile", learnerId],
    () => api.getLearnerProfile(learnerId),
    { staleTime: 60000 }
  );

  const profileData = response?.data ?? {};
  const learner = profileData?.profile ?? cached ?? {};
  const name = learner?.name ?? learner?.user?.name ?? "—";
  const email = learner?.email ?? learner?.user?.email ?? "—";
  const dept = learner?.department ?? "—";
  const jobTitle = learner?.jobTitle ?? "—";
  const location = learner?.location ?? "—";
  const status = learner?.status ?? null;
  const startedAt = learner?.startedAt ?? learner?.createdAt ?? null;
  const courses =
    profileData?.courseProgress ??
    learner?.courseProgress ??
    learner?.courses ??
    [];
  const learningPaths =
    profileData?.enrolledLearningPaths ??
    learner?.learningPaths ??
    learner?.enrolledLearningPaths ??
    [];

  const initials = getInitials(name);
  const colour = getAvatarColour(name);

  const formatDate = (d) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (isLoading && !cached) {
    return (
      <div className="p-5 space-y-4">
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        <div className="grid grid-cols-2 gap-5">
          <div className="h-64 bg-gray-100 rounded-xl animate-pulse" />
          <div className="h-64 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Back link */}
      <button
        onClick={() => navigate("/admin/learners")}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary-blue mb-5 transition-colors"
      >
        <i className="bi bi-chevron-left text-xs" />
        Back to Learners
      </button>

      {/* Profile header card */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-5 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 ${colour}`}>
            {initials}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{name}</h2>
            <p className="text-sm text-blue-500 mb-2">{email}</p>
            <div className="flex flex-wrap gap-2">
              {dept !== "—" && (
                <span className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">{dept}</span>
              )}
              {jobTitle !== "—" && (
                <span className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">{jobTitle}</span>
              )}
              {location !== "—" && (
                <span className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">{location}</span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <StatusBadge status={status} />
          {startedAt && (
            <p className="text-xs text-gray-400 mt-2">Started {formatDate(startedAt)}</p>
          )}
        </div>
      </div>

      {/* Two column panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Course Progress */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Course Progress</h3>
          {courses.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No courses enrolled</p>
          ) : (
            <div className="space-y-4">
              {courses.map((course, idx) => {
                const courseName = course?.name ?? course?.courseName ?? "—";
                const pct = course?.progress ?? course?.percentage ?? 0;
                const courseStatus = course?.status ?? null;
                return (
                  <div key={idx} className="border-b border-gray-50 pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700 truncate max-w-[60%]" title={courseName}>{courseName}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 font-medium">{pct}%</span>
                        <StatusBadge status={courseStatus} />
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-primary-blue h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Enrolled Learning Paths */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Enrolled Learning Paths</h3>
          {learningPaths.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No learning paths enrolled</p>
          ) : (
            <div className="space-y-3">
              {learningPaths.map((lp, idx) => {
                const lpName = lp?.name ?? lp?.learningPathName ?? "—";
                const courseCount =
                  lp?.coursesCount ??
                  lp?.numberOfCourses ??
                  lp?.courseCount ??
                  0;
                const startDate = lp?.startDate ?? lp?.start_date ?? null;
                const endDate = lp?.endDate ?? lp?.end_date ?? null;
                const lpStatus = lp?.status ?? "active";
                const isActive = lpStatus?.toLowerCase() === "active";
                return (
                  <div key={idx} className="flex items-start justify-between border border-gray-100 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{lpName}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {courseCount} course{courseCount !== 1 ? "s" : ""}
                        {(startDate || endDate) && (
                          <> · {formatDate(startDate)} – {formatDate(endDate)}</>
                        )}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ml-3 ${isActive
                        ? "text-blue-600 bg-blue-100"
                        : "text-gray-500 bg-gray-100"
                        }`}
                    >
                      {lpStatus}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearnerProfile;
